import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const ExtraCreditForEnterpriseForm = NiceModal.create(({ mode, modalName, id }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const { t } = useTranslation();

	const schema = useMemo(
		() =>
			yup.object().shape({
				companyID: yup.string().nullable().required(),
				givenShipments: yup.number().nullable().min(0).required(),
				remainingShipments: yup
					.number()
					.nullable()
					.min(0)
					.max(yup.ref('givenShipments'), () => t('remainingAmountCannotBeLessThatGivenAmount')),
				expiryDate: yup.date().nullable().required(),
				description: yup.string().nullable().required()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			companyID: null,
			givenShipments: null,
			remainingShipments: null,
			expiryDate: null,
			description: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(ADMIN.GET_EXTRA_FREE_SHIPMENT_CREDIT_BY_ID, {
				id
			});

			const { data, status } = response;

			if (status === 200)
				return {
					...defaultValues,
					...data
				};
			else AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = useCallback(
		async (data) => {
			const URL =
				mode === 'add' ? ADMIN.ADD_EXTRA_FREE_SHIPMENT_CREDIT : ADMIN.UPDATE_EXTRA_FREE_SHIPMENT_CREDIT_BY_ID;

			try {
				const response = await axiosPrivate.post(URL, {
					...data,
					id
				});
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.EXTRA_CREDITS_ENTERPRISE));
					handleCloseModal();
				} else {
					AppLogger.error(response);
					showError();
				}
			} catch (error) {
				AppLogger.error(error);
				showError({
					message: error.response?.data?.message
				});
			}
		},
		[id]
	);

	return (
		<Modal fullwidth maxWidth="large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">{mode === 'edit' ? 'editExtraCredit' : 'addExtraCredit'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={'md: 6'} label="company" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ENTERPRISE_CLIENTS}
							value={watch('companyID')}
							setValue={(value) => {
								setValue('companyID', value.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="companyID"
							columns={[
								{ accessor: 'ID', width: 100 },
								{ accessor: 'Name', width: 1000 }
							]}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem label="amount" isLoading={isLoading} required>
						<IntegerInput name="givenShipments" />
					</FormItem>
					<FormItem label="remainingAmout" isLoading={isLoading} disabled>
						<IntegerInput name="remainingShipments" disabled={mode === 'add'} />
					</FormItem>
					<FormItem label="expiryDate" isLoading={isLoading} required>
						<DateInput
							name="expiryDate"
							value={watch('expiryDate')}
							setValue={(value) => {
								setValue('expiryDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem label="description" isLoading={isLoading} required>
						<StringInput name="description" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={mode === 'edit' ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ExtraCreditForEnterpriseForm;
